import { tokenContract } from '@daymade/api-contracts';
import { ClientInferResponseBody } from '@ts-rest/core';
import AboutUsModule from 'components/about-us/AboutUsModule';
import AboutUsSiteContainer from 'components/about-us/AboutUsSiteContainer';
import AboutUsSiteInnerContainer from 'components/about-us/AboutUsSiteInnerContainer';
import ImageWithText from 'components/common/ImageWithText';
import ImageWithFallback from 'components/ImageWithFallback';
import RoundedButton from 'components/RoundedButton';
import { dashboardFolder } from 'constants/Dashboard';
import { useEffect, useState } from 'react';
import { tokenApiClient } from 'utils/apiClient';
import { singularOrPlural } from 'utils/common';
import HttpStatusCode from 'utils/HttpStatusCode';
import DashboardComponentHeader from './common/DashboardComponentHeader';
import DashboardComponentLink from './common/DashboardComponentLink';
import styles from './MoreWaysToWin.module.css';

type TokenRedemptionOption = ClientInferResponseBody<
  typeof tokenContract.public.getTokenRedemptionOptions,
  200
>[0];

const getCountdownLabel = (nextDrawDate: Date | string, nowMs: number) => {
  const diffMs = new Date(nextDrawDate).getTime() - nowMs;

  if (diffMs <= 0) {
    return '00h 00m 00s';
  }

  const totalSeconds = Math.floor(diffMs / 1000);
  const totalHours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const hoursText = String(totalHours).padStart(2, '0');
  const minutesText = String(minutes).padStart(2, '0');
  const secondsText = String(seconds).padStart(2, '0');

  return `${hoursText}h ${minutesText}m ${secondsText}s`;
};

export const MoreWaysToWin = ({ loading }: { loading?: boolean }) => {
  const [tokenRedemptionOptions, setTokenRedemptionOptions] = useState<
    TokenRedemptionOption[] | null
  >(null);
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    // Only run the countdown timer when there are redemption options to display
    if (!tokenRedemptionOptions || tokenRedemptionOptions.length === 0) {
      return;
    }

    const timerId = setInterval(() => {
      setNowMs(Date.now());
    }, 1000);

    return () => {
      clearInterval(timerId);
    };
  }, [tokenRedemptionOptions]);

  useEffect(() => {
    (async () => {
      const tokenRedemptionOptionsResponse =
        await tokenApiClient.public.getTokenRedemptionOptions();

      if (tokenRedemptionOptionsResponse.status === HttpStatusCode.OK) {
        const tokenRedemptionOptions = tokenRedemptionOptionsResponse.body
          .filter(
            (option) =>
              option.group === 'GAME' &&
              option.drawDetails &&
              option.drawDetails.nextDrawDate &&
              option.drawDetails.prizeImageLocation,
          )
          .sort(
            (a, b) =>
              new Date(a.drawDetails!.nextDrawDate!).getTime() -
                new Date(b.drawDetails!.nextDrawDate!).getTime() || // Primary sort by next draw date
              a.price - b.price, // Secondary sort by price if draw dates are the same
          )
          .slice(0, 3); // Limit to top 3 upcoming draws

        setTokenRedemptionOptions(tokenRedemptionOptions);
      }
    })();
  }, []);

  if (
    !tokenRedemptionOptions ||
    tokenRedemptionOptions.length === 0 ||
    loading
  ) {
    return null;
  }

  return (
    <AboutUsModule
      background="tealGradient"
      withReducedPadding
      extendBackgroundAbove
      extendBackgroundBelow
    >
      <AboutUsSiteContainer>
        <AboutUsSiteInnerContainer background="white" borderRadius withPadding>
          <div className={styles.container}>
            <DashboardComponentHeader
              title="More Ways to Win"
              subtitle={
                <>
                  Upcoming draws and raffles in <strong>Token Town</strong>,
                  enter now for more chances to win!
                </>
              }
              separator
            />
            <div className={styles.tokenRedemptionOptionsContainer}>
              {tokenRedemptionOptions.map((option) => {
                return (
                  <div
                    className={styles.tokenRedemptionOption}
                    key={option.code}
                  >
                    <ImageWithText
                      desktopSize="third"
                      imageSrc={option.drawDetails!.prizeImageLocation!}
                      imageAlt={option.description ?? option.title ?? 'Prize'}
                      imageOverlayChildren={
                        <span
                          className={`${styles.timerBadge} aboutUsTypographyCaption`}
                        >
                          <ImageWithFallback
                            src={`${dashboardFolder}/clock-icon.svg`}
                            width={11}
                            height={11}
                            alt="Timer"
                          />
                          <strong className={styles.timerText}>
                            {getCountdownLabel(
                              option.drawDetails!.nextDrawDate!,
                              nowMs,
                            )}
                          </strong>
                        </span>
                      }
                      textChildren={
                        <div className={styles.itemTextWrapper}>
                          <div>
                            <div
                              className={`noMargin textAlignLeft ${styles.title}`}
                            >
                              <strong>
                                {option.drawDetails?.prizeTitle ?? option.title}
                              </strong>
                            </div>
                            <div className={styles.priceRow}>
                              <ImageWithFallback
                                src={`${dashboardFolder}/token-icon.svg`}
                                width={15}
                                height={15}
                                alt="Token"
                              />
                              <p className={`noMargin ${styles.priceText}`}>
                                {option.price > 0
                                  ? `${option.price} ${singularOrPlural(option.price, 'token', 'tokens')}`
                                  : 'FREE'}
                              </p>
                            </div>
                          </div>
                          <div className={styles.enterButton}>
                            <RoundedButton
                              href={
                                option.slug
                                  ? `/token-town/${option.slug}`
                                  : '/token-town'
                              }
                              fullWidth
                              rebrandDm2025
                              mini
                            >
                              ENTER NOW
                            </RoundedButton>
                          </div>
                        </div>
                      }
                    />
                  </div>
                );
              })}
            </div>

            <DashboardComponentLink
              href="/token-town"
              linkText={'Go to Token Town'}
            />
          </div>
        </AboutUsSiteInnerContainer>
      </AboutUsSiteContainer>
    </AboutUsModule>
  );
};
